import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { PriceListService } from '../../service/pricelist.service';
import { PriceList } from '../../model/pricelist';
import { SelectionItem } from '../../../core/component/select/select.component';
import { CurrencySelection } from '../../../core/pipe/currency.pipe';
import { PriceListItem } from '../../model/priceListItem';
import { CurrencySymbol, Money } from '../../../core/model/money';
import { LocalDate } from '../../../core/model/local-date';
import { MeterMediaTypeSelection } from '../../model/const/enums';
import { CompanyService } from '../../../rent/service/data/company.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { Article } from '../../../rent/model/article';
import { switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-imd-pricelist-form',
    templateUrl: './pricelist-form.component.html',
    styleUrls: ['./pricelist-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: PriceListService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImdPriceListFormComponent extends FormComponentBase<PriceList> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _companyService: CompanyService,
    private _articleService: ArticleService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    private _service: PriceListService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): PriceList {
    return new PriceList();
  }

  private _articles: Article[];
  articleSelection: SelectionItem[];
  meterTypeSelection = MeterMediaTypeSelection;
  currencySelection: SelectionItem[];

  private updateArticleSelection() {
    var currencyCode = this.form.controls['currencyCode'].value;
    if (this._articles) {
      this.articleSelection = this._articles.filter(a => a.currencyCode === currencyCode).map(a => a.toSelectionItem());
    }
  }

  init(data: PriceList) {
    super.init(data);

    this.AddSubscription(
      this._companyService
        .getUsedCurrencies()
        .pipe(
          switchMap(res => {
            this.currencySelection = res.map(c => new SelectionItem(CurrencySymbol(c), c));
            if (this.isNew) {
              this.form.controls['currencyCode'].setValue(this.currencySelection[0].value);
            } else {
              let currencyCode = this.form.controls['currencyCode'].value;
              if (!this.currencySelection.find(c => c.value === currencyCode)) {
                this.currencySelection.push(new SelectionItem(CurrencySymbol(currencyCode), currencyCode));
              }
            }
            return this._articleService.getList();
          })
        )
        .subscribe(articles => {
          this._articles = articles;
          this.updateArticleSelection();
        })
    );

    if (this.isNew) {
      this.prices.controls.push(this.createPriceForm(null));
      this.AddSubscription(
        this.form.controls['currencyCode'].valueChanges.subscribe(val => {
          for (let priceForm of this.prices.controls.map(c => c as FormGroup)) {
            priceForm.controls['amountPerUnit'].setValue(new Money(val, priceForm.controls['amountPerUnit'].value.amount));
          }
          this.updateArticleSelection();
        })
      );
    } else {
      for (let price of this.dataContext.prices) {
        this.prices.controls.push(this.createPriceForm(price));
      }
    }
  }

  prices: FormArray;

  addPrice() {
    let price = new PriceListItem();
    price.amountPerUnit = new Money(this.form.controls['currencyCode'].value, 0);
    let fromDate = LocalDate.today();
    fromDate.addMonths(1);
    fromDate = LocalDate.fromDate(fromDate.firstDayInMonth());
    price.fromDate = fromDate;
    this.prices.controls.push(this.createPriceForm(price));
  }

  removePrice(index: number) {
    this.prices.controls.splice(index, 1);
    this.form.markAsDirty();
  }

  private createPriceForm(price: PriceListItem) {
    var form = this._fb.group({
      id: this._fc.createHidden(price ? price.id : null),
      amountPerUnit: this._fc.createMandatory(
        'CONSUMPTION.PRICELIST.PRICE_PER_UNIT',
        price ? price.amountPerUnit : new Money(this.form.controls['currencyCode'].value, 0)
      ),
      fromDate: this._fc.createMandatory('CONSUMPTION.PRICELIST.VALID_FROM', price ? price.fromDate : null),
    });
    this.AddSubscription(
      form.valueChanges.subscribe(x => {
        this.prices.updateValueAndValidity();
        this.form.markAsDirty();
      })
    );
    return form;
  }

  onSubmit() {
    super.onSubmit();
  }
  prepareSave(): PriceList {
    let result = super.prepareSave();
    result.prices = this.prices.controls.map(c => c.value);
    return result;
  }

  protected getFormGroup(): FormGroup {
    this.prices = this._fb.array([]);
    return this._fb.group({
      id: this._fc.createHidden(this.dataContext.id),
      name: this._fc.createMandatory('CONSUMPTION.PRICELIST.NAME', this.dataContext.name),
      currencyCode: this._fc.createWithOptions('COMMON.CURRENCY', this.dataContext.currencyCode, {
        isReadonly: !this.isNew,
        required: true,
      }),
      meterType: this._fc.createWithOptions('CONSUMPTION.MEDIA_TYPE.TYPE', this.dataContext.meterType, {
        isReadonly: !this.isNew,
        required: true,
      }),
      articleId: this._fc.createMandatory('LEASE_AGREEMENT_ROW.ARTICLE', this.dataContext.articleId),
      prices: this.prices,
    });
  }
}
